import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Share2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  Navigation,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Crosshair,
  Gamepad2,
  Beer,
  Clock,
  Utensils,
  Target,
  Users,
  Home,
  ChevronRight as ChevronRightIcon,
  Link as LinkIcon,
  Table as TableIcon,
  BarChart3,
  CalendarDays,
  MessageCircle,
} from 'lucide-react';
import pubService, { Pub, PubTeam, WeekSchedule, OpeningHour, TeamLink } from '../services/pubService';
import StartChatButton from '../components/StartChatButton';
import FavoriteButton from '../components/profile/FavoriteButton';

const TEAL = '#F59E0B'; // amber/orange to match pub listing
const WARN = '#F59E0B';

function pad2(n?: number): string {
  return String(typeof n === 'number' ? n : 0).padStart(2, '0');
}

function formatOpeningHour(oh?: OpeningHour | null): string {
  if (!oh || !oh.start || !oh.end) return '';
  return `${pad2(oh.start.hour)}:${pad2(oh.start.minute)} - ${pad2(oh.end.hour)}:${pad2(oh.end.minute)}`;
}

const DAY_NAMES_DE: { key: keyof WeekSchedule; label: string }[] = [
  { key: 'monday', label: 'Montag' },
  { key: 'tuesday', label: 'Dienstag' },
  { key: 'wednesday', label: 'Mittwoch' },
  { key: 'thursday', label: 'Donnerstag' },
  { key: 'friday', label: 'Freitag' },
  { key: 'saturday', label: 'Samstag' },
  { key: 'sunday', label: 'Sonntag' },
];

function getCurrentDayKey(): keyof WeekSchedule {
  const idx = (new Date().getDay() + 6) % 7;
  return DAY_NAMES_DE[idx].key;
}

interface ScheduleRow {
  dayKey: keyof WeekSchedule;
  dayLabel: string;
  time: string;
  isToday: boolean;
}

function buildScheduleRows(schedule?: WeekSchedule | null): ScheduleRow[] {
  if (!schedule) return [];
  const today = getCurrentDayKey();
  const rows: ScheduleRow[] = [];
  for (const day of DAY_NAMES_DE) {
    const hours = (schedule[day.key] as OpeningHour[] | undefined) || [];
    for (const oh of hours) {
      const time = formatOpeningHour(oh);
      if (time) rows.push({ dayKey: day.key, dayLabel: day.label, time, isToday: day.key === today });
    }
  }
  return rows;
}

const PubDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pub, setPub] = useState<Pub | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentMenuIndex, setCurrentMenuIndex] = useState(0);
  const [isDescriptionExpanded, setDescExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxList, setLightboxList] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [externalLink, setExternalLink] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await pubService.getPubBySlug(slug);
      const p: Pub | null = res?.data || null;
      if (!p || typeof p !== 'object') {
        setNotFound(true);
        setPub(null);
        return;
      }
      if (p.visible === false) {
        setNotFound(true);
        setPub(null);
        return;
      }
      setPub(p);
    } catch (err) {
      console.error('Fehler beim Laden der Spielstätte:', err);
      setNotFound(true);
      setPub(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (copyToast) {
      const tid = setTimeout(() => setCopyToast(null), 2000);
      return () => clearTimeout(tid);
    }
  }, [copyToast]);

  const allImages = useMemo<string[]>(() => {
    if (!pub) return [];
    const list: string[] = [];
    if (pub.image) list.push(pub.image);
    if (Array.isArray(pub.images)) {
      for (const img of pub.images) {
        if (img && !list.includes(img)) list.push(img);
      }
    }
    return list;
  }, [pub]);

  const menuFiles = useMemo<string[]>(() => {
    if (!pub || !Array.isArray(pub.menuFiles)) return [];
    return pub.menuFiles.filter((m): m is string => typeof m === 'string' && m.length > 0);
  }, [pub]);

  const openingRows = useMemo(() => buildScheduleRows(pub?.openingHours), [pub]);
  const playingRows = useMemo(() => buildScheduleRows(pub?.playingHours), [pub]);

  const hasContact = !!(pub?.phone || pub?.email);
  const description = pub?.description || '';
  const isDescriptionLong = description.length > 250;
  const displayedDescription =
    isDescriptionExpanded || !isDescriptionLong
      ? description
      : `${description.substring(0, 250)}...`;

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = pub?.name ? `${pub.name} | DartCloud` : 'DartCloud Spielstätte';
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      }
    } catch (_e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyToast('Link in Zwischenablage kopiert');
    } catch (_e) {
      setCopyToast('Teilen nicht möglich');
    }
  };

  const handleCopy = async (text: string, toastMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(toastMsg);
    } catch (_e) {
      setCopyToast('Kopieren fehlgeschlagen');
    }
  };

  const handleOpenMaps = () => {
    if (!pub?.address) return;
    const { latitude, longitude, label } = pub.address;
    let url: string;
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    } else if (label) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
    } else {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCallPhone = (phone?: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
  };

  const handleSendEmail = (email?: string | null) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  const handleOpenTeamLink = (url?: string) => {
    if (!url) return;
    setExternalLink(url);
  };

  const previousImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx > 0 ? idx - 1 : allImages.length - 1));
  };
  const nextImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx < allImages.length - 1 ? idx + 1 : 0));
  };
  const openImageLightbox = (idx: number) => {
    setLightboxList(allImages.map((id) => pubService.buildFileUrl(id) || ''));
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const previousMenuImage = () => {
    if (menuFiles.length <= 1) return;
    setCurrentMenuIndex((idx) => (idx > 0 ? idx - 1 : menuFiles.length - 1));
  };
  const nextMenuImage = () => {
    if (menuFiles.length <= 1) return;
    setCurrentMenuIndex((idx) => (idx < menuFiles.length - 1 ? idx + 1 : 0));
  };
  const openMenuLightbox = (idx: number) => {
    setLightboxList(menuFiles.map((id) => pubService.buildFileUrl(id) || ''));
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-zinc-700 border-t-white rounded-full animate-spin" />
          <div className="text-sm text-zinc-400">Spielstätte laden...</div>
        </div>
      </div>
    );
  }

  if (notFound || !pub) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-zinc-500" />
          <h1 className="text-2xl font-bold mb-2">Spielstätte nicht gefunden</h1>
          <p className="text-zinc-400 mb-6">Die gesuchte Spielstätte existiert nicht oder ist nicht öffentlich.</p>
          <button
            onClick={() => navigate('/spielstaetten')}
            className="px-5 py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-zinc-200 transition"
          >
            Zur Spielstätten-Übersicht
          </button>
        </div>
      </div>
    );
  }

  const metaDescription =
    description.length > 0
      ? description.length > 160 ? `${description.substring(0, 157)}...` : description
      : `Dart-Spielstätte ${pub.name || ''} auf DartCloud${pub.address?.label ? ` - Dart spielen in ${pub.address.label}` : ''}`;
  const heroImageUrl = pubService.buildFileUrl(allImages[0]);
  const currentImageUrl = pubService.buildFileUrl(allImages[currentImageIndex]);
  const currentMenuUrl = pubService.buildFileUrl(menuFiles[currentMenuIndex]);
  const logoUrl = pubService.buildFileUrl(pub.logo);
  const addressLabel = pubService.formatAddress(pub.address);
  const hasCoords = typeof pub.address?.latitude === 'number' && typeof pub.address?.longitude === 'number';

  return (
    <>
      <Helmet>
        <title>{pub.name ? `${pub.name} | DartCloud` : 'Spielstätte | DartCloud'}</title>
        <meta name="description" content={metaDescription} />
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}
        <meta property="og:title" content={pub.name ? `${pub.name} | DartCloud` : 'Spielstätte | DartCloud'} />
        <meta property="og:description" content={metaDescription} />
        <link rel="canonical" href={`https://dartcloudapp.de/spielstaette/${slug}`} />
      </Helmet>

      {/* HERO HEADER */}
      <section
        className="w-full border-b border-zinc-800"
        style={{ background: 'linear-gradient(135deg, #1c1410 0%, #2a1f15 100%)' }}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-5 sm:py-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition text-white"
              aria-label="Zurück"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <FavoriteButton
                id={pub.id || pub._id || ''}
                type="PUB"
                className="p-3 rounded-xl border border-zinc-700 bg-zinc-800 hover:bg-zinc-700"
              />
              <ActionButton icon={<Share2 size={20} />} onClick={handleShare} />
            </div>
          </div>

          <div className="mt-4 flex items-start gap-4">
            {logoUrl && (
              <div className="shrink-0 rounded-2xl bg-white overflow-hidden shadow-lg" style={{ width: 70, height: 70 }}>
                <img src={logoUrl} alt={pub.name || 'Logo'} className="w-full h-full object-cover sm:!w-[90px] sm:!h-[90px]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${TEAL}26` }}
              >
                <Beer size={14} style={{ color: TEAL }} />
                <span className="text-xs font-semibold tracking-wider" style={{ color: TEAL }}>SPIELSTÄTTE</span>
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white leading-tight -tracking-[0.5px] break-words">
                {pub.name}
              </h1>
              <div className="mt-2 flex items-center gap-1.5 text-zinc-400 text-sm">
                <MapPin size={18} className="shrink-0" />
                <span className="truncate">{addressLabel}</span>
              </div>
            </div>
          </div>

          {(pub.steeldart === true || pub.edart === true || pub.flat === true || pub.rentable === true) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {pub.steeldart === true && <HeroTag icon={<Crosshair size={14} style={{ color: TEAL }} />} label="Steeldart" />}
              {pub.edart === true && <HeroTag icon={<Gamepad2 size={14} style={{ color: TEAL }} />} label="E-Dart" />}
              {pub.flat === true && <HeroTag icon={<Home size={14} style={{ color: TEAL }} />} label="Flat-Rate" />}
              {pub.rentable === true && <HeroTag icon={<Target size={14} style={{ color: TEAL }} />} label="Mietbar" />}
            </div>
          )}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-6 sm:py-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8 space-y-8">
            {/* Image Gallery */}
            {allImages.length > 0 && (
              <Section title="Galerie">
                <div className="rounded-2xl overflow-hidden bg-zinc-900 shadow-xl relative" style={{ height: 350 }}>
                  {currentImageUrl ? (
                    <img
                      src={currentImageUrl}
                      alt={`${pub.name} Bild ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openImageLightbox(currentImageIndex)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <MapPin size={64} />
                    </div>
                  )}
                  {currentImageUrl && (
                    <button
                      onClick={() => openImageLightbox(currentImageIndex)}
                      className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 text-white text-xs font-medium hover:bg-black/85 transition"
                    >
                      <Maximize2 size={16} />
                      Vollbild
                    </button>
                  )}
                  {allImages.length > 1 && (
                    <>
                      <button onClick={previousImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
                        <ChevronLeft size={26} />
                      </button>
                      <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Nächstes Bild">
                        <ChevronRight size={26} />
                      </button>
                      <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                    {allImages.map((id, idx) => {
                      const url = pubService.buildFileUrl(id);
                      const selected = idx === currentImageIndex;
                      return (
                        <button
                          key={`${id}-${idx}`}
                          onClick={() => openImageLightbox(idx)}
                          className="shrink-0 rounded-xl overflow-hidden bg-zinc-800"
                          style={{ width: 100, height: 80, border: `${selected ? 3 : 1}px solid ${selected ? TEAL : '#3f3f46'}` }}
                        >
                          {url ? (
                            <img src={url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500"><MapPin size={20} /></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Section>
            )}

            {/* Description */}
            {description.length > 0 && (
              <Section title="Über die Spielstätte">
                <p className="whitespace-pre-wrap text-zinc-300 leading-[1.7] text-[15px]">{displayedDescription}</p>
                {isDescriptionLong && (
                  <button
                    onClick={() => setDescExpanded((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1 text-[15px] font-semibold"
                    style={{ color: TEAL }}
                  >
                    {isDescriptionExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                    {isDescriptionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                )}
              </Section>
            )}

            {/* Opening Hours */}
            {openingRows.length > 0 && (
              <Section title="Öffnungszeiten">
                <div className="space-y-3">
                  {openingRows.map((row, idx) => (
                    <ScheduleTimeRow key={`open-${row.dayKey}-${row.time}-${idx}`} row={row} label="Geöffnet" />
                  ))}
                </div>
              </Section>
            )}

            {/* Playing Hours */}
            {playingRows.length > 0 && (
              <Section title="Spielzeiten">
                <div className="space-y-3">
                  {playingRows.map((row, idx) => (
                    <ScheduleTimeRow key={`play-${row.dayKey}-${row.time}-${idx}`} row={row} label="Dart-Zeit" />
                  ))}
                </div>
              </Section>
            )}

            {/* Menu / Speisekarte */}
            {menuFiles.length > 0 && (
              <Section title="Speisekarte">
                <div className="rounded-2xl overflow-hidden bg-zinc-900 shadow-xl relative" style={{ height: 400 }}>
                  {currentMenuUrl ? (
                    <img
                      src={currentMenuUrl}
                      alt={`Speisekarte ${currentMenuIndex + 1}`}
                      className="w-full h-full object-contain cursor-pointer bg-zinc-950"
                      onClick={() => openMenuLightbox(currentMenuIndex)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Utensils size={64} />
                    </div>
                  )}
                  {currentMenuUrl && (
                    <button
                      onClick={() => openMenuLightbox(currentMenuIndex)}
                      className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 text-white text-xs font-medium hover:bg-black/85 transition"
                    >
                      <Maximize2 size={16} />
                      Vollbild
                    </button>
                  )}
                  {menuFiles.length > 1 && (
                    <>
                      <button onClick={previousMenuImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Vorherige Seite">
                        <ChevronLeft size={26} />
                      </button>
                      <button onClick={nextMenuImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Nächste Seite">
                        <ChevronRight size={26} />
                      </button>
                      <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold">
                        {currentMenuIndex + 1} / {menuFiles.length}
                      </div>
                    </>
                  )}
                </div>
              </Section>
            )}

            {/* Teams */}
            {Array.isArray(pub.teams) && pub.teams.length > 0 && (
              <Section title="Mannschaften">
                <div className="space-y-4">
                  {pub.teams.map((team, idx) => (
                    <TeamCard
                      key={team.id || `${team.name}-${idx}`}
                      team={team}
                      onLinkClick={handleOpenTeamLink}
                    />
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 mt-8 lg:mt-0 space-y-5">
            {hasContact && (
              <SidebarCard title="Kontakt" icon={<Phone size={20} style={{ color: TEAL }} />}>
                <div className="space-y-3">
                  {pub.phone && (
                    <ContactItem
                      icon={<Phone size={20} style={{ color: TEAL }} />}
                      label={pub.phone}
                      onClick={() => handleCallPhone(pub.phone)}
                      onCopy={() => handleCopy(pub.phone || '', 'Telefonnummer kopiert')}
                    />
                  )}
                  {pub.email && (
                    <ContactItem
                      icon={<Mail size={20} style={{ color: TEAL }} />}
                      label={pub.email}
                      onClick={() => handleSendEmail(pub.email)}
                      onCopy={() => handleCopy(pub.email || '', 'E-Mail kopiert')}
                    />
                  )}
                </div>
              </SidebarCard>
            )}

            {(pub.id || pub._id) && (
              <SidebarCard title="Nachricht" icon={<MessageCircle size={20} style={{ color: TEAL }} />}>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                  Hast du Fragen zur Spielstätte? Sende eine Nachricht über DartCloud.
                </p>
                <StartChatButton entryId={pub.id || pub._id} entryType="PUB" />
              </SidebarCard>
            )}

            <SidebarCard title="Anfahrt" icon={<MapPin size={20} style={{ color: TEAL }} />}>
              <div className="flex items-start gap-3 mb-4">
                <MapPin size={20} style={{ color: TEAL }} className="shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-300 leading-[1.5]">{addressLabel}</p>
              </div>
              <MapPreview
                latitude={pub.address?.latitude || undefined}
                longitude={pub.address?.longitude || undefined}
                hasCoords={hasCoords}
                onClickRoute={handleOpenMaps}
              />
            </SidebarCard>
          </aside>
        </div>
      </main>

      {lightboxOpen && lightboxList[lightboxIndex] && (
        <Lightbox
          src={lightboxList[lightboxIndex]}
          counter={`${lightboxIndex + 1} / ${lightboxList.length}`}
          showNav={lightboxList.length > 1}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex((i) => (i > 0 ? i - 1 : lightboxList.length - 1))}
          onNext={() => setLightboxIndex((i) => (i < lightboxList.length - 1 ? i + 1 : 0))}
        />
      )}

      {externalLink && (
        <ExternalLinkModal
          url={externalLink}
          onCancel={() => setExternalLink(null)}
          onConfirm={() => {
            const url = externalLink;
            setExternalLink(null);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
        />
      )}

      {copyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-full shadow-lg text-sm font-medium bg-zinc-900 border border-zinc-700 text-white">
          {copyToast}
        </div>
      )}
    </>
  );
};

// ===================================================================
// Subcomponents (shared style w/ ClubDetailPage but in orange/amber theme)
// ===================================================================

const ActionButton: React.FC<{ icon: React.ReactNode; onClick: () => void; active?: boolean }> = ({ icon, onClick, active }) => (
  <button
    onClick={onClick}
    className="p-3 rounded-xl border transition"
    style={{
      borderColor: active ? TEAL : '#3f3f46',
      backgroundColor: active ? `${TEAL}26` : '#27272a',
      color: active ? TEAL : '#d4d4d8',
    }}
  >
    {icon}
  </button>
);

const HeroTag: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800">
    {icon}
    <span className="text-xs font-medium text-zinc-300">{label}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2 className="text-xl sm:text-[22px] font-bold text-white -tracking-[0.5px] mb-4">{title}</h2>
    {children}
  </section>
);

const ScheduleTimeRow: React.FC<{ row: ScheduleRow; label: string }> = ({ row, label }) => (
  <div
    className="flex items-center gap-3 p-4 rounded-2xl border"
    style={{
      backgroundColor: row.isToday ? `${TEAL}1A` : '#1f1f23',
      borderColor: row.isToday ? `${TEAL}4D` : '#27272a',
    }}
  >
    <div className="flex items-center gap-2 w-24 shrink-0">
      {row.isToday && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TEAL }} />}
      <span
        className="text-[15px]"
        style={{
          fontWeight: row.isToday ? 700 : 500,
          color: row.isToday ? TEAL : '#fff',
        }}
      >
        {row.dayLabel}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[15px] font-semibold text-white">{row.time}</div>
      <div className="text-[13px] text-zinc-400 inline-flex items-center gap-1.5">
        <Clock size={12} />
        {label}
      </div>
    </div>
  </div>
);

const TeamCard: React.FC<{ team: PubTeam; onLinkClick: (url?: string) => void }> = ({ team, onLinkClick }) => {
  const linkIcon = (icon?: string | null) => {
    switch (icon) {
      case 'table': return <TableIcon size={18} />;
      case 'games': return <Gamepad2 size={18} />;
      case 'stats': return <BarChart3 size={18} />;
      case 'calendar': return <CalendarDays size={18} />;
      default: return <LinkIcon size={18} />;
    }
  };

  return (
    <div className="rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden">
      <div className="flex items-center gap-4 p-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${TEAL}1A`, color: TEAL }}
        >
          <Users size={26} />
        </div>
        <div className="min-w-0">
          <div className="text-[17px] font-bold text-white truncate">{team.name}</div>
          {team.league && <div className="text-[13px] text-zinc-400 truncate">{team.league}</div>}
        </div>
      </div>
      {team.description && team.description.length > 0 && (
        <p className="px-4 pb-4 text-sm text-zinc-400 leading-[1.4] whitespace-pre-wrap">{team.description}</p>
      )}
      {Array.isArray(team.links) && team.links.length > 0 && (
        <div className="px-4 pb-4 flex flex-wrap gap-2.5">
          {team.links.map((lnk: TeamLink, idx: number) => (
            <button
              key={lnk.id || `${lnk.url}-${idx}`}
              onClick={() => onLinkClick(lnk.url)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 transition"
              style={{ borderColor: TEAL }}
            >
              {linkIcon(lnk.icon)}
              <span>{lnk.name}</span>
              <ExternalLink size={14} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <section className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${TEAL}1A` }}>
        {icon}
      </div>
      <h3 className="text-[17px] font-bold text-white">{title}</h3>
    </div>
    {children}
  </section>
);

const ContactItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onCopy: () => void;
}> = ({ icon, label, onClick, onCopy }) => (
  <div className="flex items-center gap-3">
    <button onClick={onClick} className="flex-1 min-w-0 flex items-center gap-3 py-1.5 hover:opacity-80 transition">
      {icon}
      <span className="text-sm text-zinc-300 truncate">{label}</span>
      <ChevronRightIcon size={14} className="text-zinc-500 ml-auto shrink-0" />
    </button>
    <button
      onClick={onCopy}
      className="p-2 rounded-lg border border-zinc-800 hover:bg-zinc-800 transition text-zinc-400 hover:text-white"
      aria-label="Kopieren"
    >
      <Copy size={14} />
    </button>
  </div>
);

const MapPreview: React.FC<{
  latitude?: number;
  longitude?: number;
  hasCoords: boolean;
  onClickRoute: () => void;
}> = ({ latitude, longitude, hasCoords, onClickRoute }) => {
  const iframeSrc = hasCoords && typeof latitude === 'number' && typeof longitude === 'number'
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.005},${latitude - 0.0025},${longitude + 0.005},${latitude + 0.0025}&layer=mapnik&marker=${latitude},${longitude}`
    : null;

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-zinc-800" style={{ height: 180 }}>
      {iframeSrc ? (
        <iframe
          src={iframeSrc}
          title="Karte"
          className="w-full h-full border-0 pointer-events-none"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
          <MapPin size={48} />
        </div>
      )}
      <button
        onClick={onClickRoute}
        className="absolute bottom-0 left-0 right-0 py-3 flex items-center justify-center gap-2 text-white text-sm font-semibold"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.8))' }}
      >
        <Navigation size={18} />
        Route in Maps öffnen
      </button>
    </div>
  );
};

const Lightbox: React.FC<{
  src: string;
  counter: string;
  showNav: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}> = ({ src, counter, showNav, onClose, onPrev, onNext }) => (
  <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
    <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white" aria-label="Schließen">
      <X size={24} />
    </button>
    <img src={src} alt="Vollbild" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
    {showNav && (
      <>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
          <ChevronLeft size={28} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Nächstes Bild">
          <ChevronRight size={28} />
        </button>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-zinc-800/80 text-white text-sm">
          {counter}
        </div>
      </>
    )}
  </div>
);

const ExternalLinkModal: React.FC<{
  url: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ url, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[65] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-3 mb-3">
        <AlertCircle size={28} style={{ color: WARN }} />
        <h3 className="text-lg font-bold text-white">Externer Link</h3>
      </div>
      <p className="text-sm text-zinc-300">Du wirst zu einer externen Webseite weitergeleitet.</p>
      <div className="mt-3 p-3 rounded-lg bg-zinc-800 text-xs text-zinc-400 break-all">{url}</div>
      <p className="mt-3 text-xs italic text-zinc-500">
        Wir übernehmen keine Verantwortung für den Inhalt externer Webseiten.
      </p>
      <div className="mt-6 flex gap-3">
        <button onClick={onCancel} className="flex-1 px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium">
          Abbrechen
        </button>
        <button onClick={onConfirm} className="flex-1 px-4 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2" style={{ backgroundColor: TEAL, color: '#000' }}>
          <ExternalLink size={18} />
          Link öffnen
        </button>
      </div>
    </div>
  </div>
);

export default PubDetailPage;
